import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Tabs,
  Tab,
  Spinner,
  Alert,
  Badge
} from 'react-bootstrap';
import { fetchSalesInstituteById } from '../../../services/salesInstitutesService';

const SalesInstituteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  interface User {
    user_id: number;
    username: string;
    email: string;
    role: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Contact {
    contact_id: number;
    name: string;
    email: string;
    phone: string;
    role: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Order {
    order_id: number;
    order_date: string;
    total_amount: string;
    tax_id: number;
    discount_id: number;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Service {
    service_id: number;
    service_name: string;
    description: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Plan {
    plan_id: number;
    natural_key: string;
    service_id: number;
    plan_name: string;
    pricing: string;
    valid_from: string;
    valid_to: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Payment {
    payment_id: number;
    subscription_id: number;
    amount_paid: string;
    payment_date: string;
    transaction_id: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
  }

  interface Subscription {
    subscription_id: number;
    sales_institute_id: number;
    start_date: string;
    end_date: string;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
    service: Service;
    plan: Plan;
    products: any[]; // Modify if needed
    payments: Payment[];
  }

  interface Address {
    address_id: number;
    street: string;
    city: string;
    state: string;
    zip: string;
  }

  interface SalesInstitute {
    sales_institute_id: number;
    institute_name: string;
    assigned_to: number;
    CDC_FLAG: string;
    created_at: string;
    updated_at: string;
    user: User;
    contacts: Contact[];
    orders: Order[];
    subscription: Subscription[];
    address: Address[];
  }
  const [institute, setInstitute] = useState<SalesInstitute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchSalesInstituteById(id);
        setInstitute(data);
      } catch (err) {
        setError('Failed to fetch institute details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading)
    return <Spinner animation="border" className="d-block mx-auto" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="container mt-4">
      {/* 🔹 Header with Actions */}
      <div className="d-flex justify-content-between mb-3">
        <h3>{institute?.institute_name}</h3>
        <Button
          variant="primary"
          onClick={() => navigate(`/sales-institutes/edit/${id}`)}
        >
          ✏️ Edit Institute
        </Button>
      </div>

      {/* 🔹 Institute Overview */}
      <Card className="shadow p-4">
        <Card.Body>
          <p>
            <strong>Assigned To:</strong> {institute?.user?.username}
          </p>
          <p>
            <strong>Created At:</strong>{' '}
            {institute?.created_at
              ? new Date(institute.created_at).toLocaleString()
              : 'N/A'}
          </p>
        </Card.Body>
      </Card>

      {/* 🔹 Tabs for Different Sections */}
      <Tabs defaultActiveKey="subscriptions" className="mt-4">
        {/* 🔹 Contacts */}
        <Tab eventKey="contacts" title="Contacts">
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="success"
              onClick={() => navigate(`/sales-institutes/${id}/add-contact`)}
            >
              ➕ Add Contact
            </Button>
          </div>
          <Table striped bordered hover className="mt-3">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {institute?.contacts?.length ? (
                institute.contacts.map(contact => (
                  <tr key={contact.contact_id}>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phone}</td>
                    <td>{contact.role}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No Contacts Found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>

        {/* 🔹 Orders */}
        <Tab eventKey="orders" title="Orders">
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={() =>
                navigate(`/sales/order/create-order?institute_id=${id}`)
              }
            >
              ➕ Create Order
            </Button>
          </div>
          <Table striped bordered hover className="mt-3">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {institute?.orders?.length ? (
                institute.orders.map(order => (
                  <tr key={order.order_id}>
                    <td>{order.order_id}</td>
                    <td>{order.order_date}</td>
                    <td>₹{order.total_amount}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No Orders Found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>

        {/* 🔹 Subscriptions */}
        <Tab eventKey="subscriptions" title="Subscriptions">
          {institute?.subscription?.length ? (
            institute.subscription.map(sub => (
              <Card key={sub.subscription_id} className="mt-3">
                <Card.Body>
                  <h5>
                    {sub.service.service_name} - {sub.plan.plan_name}
                  </h5>
                  <p>
                    <strong>Start Date:</strong> {sub.start_date} |{' '}
                    <strong>End Date:</strong> {sub.end_date}
                  </p>
                  <p>
                    <strong>Plan Price:</strong> ₹{sub.plan.pricing}
                  </p>

                  {/* 🔹 Payments */}
                  <h6>Payments</h6>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Amount Paid</th>
                        <th>Transaction ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sub.payments.length ? (
                        sub.payments.map(payment => (
                          <tr key={payment.payment_id}>
                            <td>{payment.payment_date}</td>
                            <td>₹{payment.amount_paid}</td>
                            <td>{payment.transaction_id}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3}>No Payments Found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p>No Subscriptions Found</p>
          )}
        </Tab>

        {/* 🔹 Address */}
        <Tab eventKey="address" title="Address">
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={() => navigate(`/sales-institutes/${id}/add-address`)}
            >
              ➕ Add Address
            </Button>
          </div>
          {institute?.address?.length ? (
            <ul>
              {institute.address.map(addr => (
                <li key={addr.address_id}>
                  {addr.street}, {addr.city}, {addr.state}, {addr.zip}
                </li>
              ))}
            </ul>
          ) : (
            <p>No Address Found</p>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default SalesInstituteDetails;
